import React from 'react'
import styled, { keyframes } from 'styled-components'

// Utils
import { useAppState } from '../context/AppContext'
import { hsla, createColor } from '../styles/colors'

interface IProps {
  className?: string
}

const Loader: React.FC<IProps> = ({ className }) => {
  const { company, theme } = useAppState()

  if (!company?.cultureValues) return null

  const marginSize = 2
  // One ring per culture value; fall back to 5 neutral rings when there are
  // none, so the spinner stays visible instead of rendering nothing.
  const rings = company.cultureValues.length
    ? company.cultureValues
    : Array(5).fill(null)
  const strokeWidth = (120 - 16) / 2 / rings.length - marginSize

  const getRadius = (i: number) => {
    if (i === 0) return 60 - strokeWidth / 2
    return 60 - (strokeWidth * i + marginSize * i) - strokeWidth / 2
  }

  return (
    <Container
      height={120}
      width={120}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      {rings.map((value, index) => {
        const hslArray = value && company.companyColors[value.color]
        const lightValueColor = value
          ? hslArray
            ? createColor(hslArray, 90)
            : hsla(theme.colors[value.color], 0.3)
          : hsla(theme.colors.primary, 0.3)
        return (
          <Circle
            key={`loader-${index}`}
            cy={60}
            cx={60}
            r={getRadius(index)}
            stroke={lightValueColor}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={2 * 3.1416 * getRadius(index) * 0.88}
            value={2 * 3.1416 * getRadius(index) * 0.88}
            delay={0.1 * index}
          />
        )
      })}
    </Container>
  )
}

export default Loader

const dash = (value: number) => keyframes`
  0% {
    stroke-dashoffset: ${value};
    transform: rotate(0);
  }
  50% {
    stroke-dashoffset: 0;
  }
  100% {
    stroke-dashoffset: -${value};
    transform: rotate(360deg);
  }
`

const Container = styled.svg`
  transform-origin: center;
  display: flex;
  margin: 0 auto;
`

const Circle = styled.circle<{ value: number; delay: number }>`
  stroke-dashoffset: -${({ value }) => value};
  stroke-linecap: round;
  animation: ${({ value }) => dash(value)} 2.6s linear infinite;
  transform-origin: 50%;
  animation-delay: ${({ delay }) => delay}s;
`
